import React, { useState } from 'react';
import { Plus, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { useServices } from '../../core/hooks/useServices';
import { useAuth } from '../../core/contexts/AuthContext';
import { ServiceDialog } from './components/ServiceDialog';
import { ColumnDef } from '@tanstack/react-table';
import type { ServiceOffering } from '../../core/models/ServiceOffering';

export const ServicesPage: React.FC = () => {
  const { organizationId } = useAuth();
  const { services, loading, error, createService, updateService } = useServices(organizationId);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<ServiceOffering | null>(null);

  const handleCreateService = async (data: any) => {
    try {
      // Services are global - not tied to organization until assigned
      await createService({
        ...data,
        category: data.category || 'general',
        unit: data.unit || 'hour',
        // organizationId is optional - services are global until assigned
      });
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to create service:', err);
      const errorMessage = err?.message || 'Failed to create service. Please try again.';
      alert(errorMessage);
    }
  };

  const handleUpdateService = async (data: any) => {
    if (!editingService) return;
    try {
      await updateService(editingService.id, data);
      setEditingService(null);
    } catch (err: any) {
      console.error('Failed to update service:', err);
      const errorMessage = err?.message || 'Failed to update service. Please try again.';
      alert(errorMessage);
    }
  };

  const columns: ColumnDef<ServiceOffering>[] = [
    {
      accessorKey: 'name',
      header: 'Service Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.name}</div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
    },
    {
      accessorKey: 'defaultRate',
      header: 'Default Rate',
      cell: ({ row }) => `$${row.original.defaultRate}`,
    },
    {
      accessorKey: 'unit',
      header: 'Unit',
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'success' : 'error'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditingService(row.original)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (loading) {
    return <LoadingState message="Loading services..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load services" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Services"
        description="Manage your service offerings and rates"
        icon={Wrench}
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Service
          </Button>
        }
      />

      {services.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title="No services found"
          description="Add your first service offering to get started"
          action={{
            label: 'Add Service',
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      ) : (
        <DataTable columns={columns} data={services} searchKey="name" searchPlaceholder="Search services..." />
      )}

      {/* Create Service Dialog */}
      <ServiceDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateService}
      />

      {/* Edit Service Dialog */}
      {editingService && (
        <ServiceDialog
          open={!!editingService}
          onClose={() => setEditingService(null)}
          service={editingService}
          onSubmit={handleUpdateService}
        />
      )}
    </div>
  );
};
